<script lang="ts">
	// Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';

	// Services and configs
	import { devices_config } from 'src/configs/devices';

	// Constants
	import { GAS_SENSOR_TYPES } from 'src/constants';

	// Helpers
	import { checkIsCriticalEvent } from 'src/helpers/device_data_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		canShowGen1LabelList,
		createGen1CombinedLabelList,
		createGen1StatusLabels,
		extractGen1MainLabel,
		getGen1DeviceStatuses,
	} from 'src/components/Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/Devices/Shelly/Gen1/gen1-main-helpers';
	import {
		getSidepanelStatusTitle,
		getSidepanelStatusValue,
		resolveBluetoothSensorStatusSlots,
	} from 'src/helpers/sidepanel_status_slots';

	// Design System Components
	import { CircleAvatar, ParameterCardV2, Text } from '@Shelly/shelly-design-system';
	// Helpers
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	// Components
	import ControlGasSensorMute from 'src/components/Controls/Single/ControlGasSensorMute.svelte';

	// Types
	import type { Device } from 'src/types/Device.ts';
	import DeviceViewStateWrapper from '../DeviceViewStateWrapper.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';
	import SidepanelStatusPeek from 'src/components/Sidepanel/SidepanelStatusPeek.svelte';
	import LastReporterV2 from 'src/components/Drawer/LastReporterV2.svelte';

	/**
	 * Main control tab for the bluetooth sensor devices sidepanel drawer.
	 *
	 * Responsibilities:
	 * - centered avatar for the primary status (temperature, door, motion)
	 * - H&T humidity as HeroValue above PeekTray; extras + last reporter in the tray
	 * - firmware incompatibility warnings
	 *
	 * @param {Device} device - The device for which the information is displayed.
	 */
	export let device: Device;

	$: isGasSensor = GAS_SENSOR_TYPES.includes(device.type as (typeof GAS_SENSOR_TYPES)[number]);

	$: deviceConfig = devices_config?.[device?.type];

	let timerLabels: Record<string, any> = {};

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	$: _statuses = getGen1DeviceStatuses(status, device, settings, deviceConfig);
	$: labelList = createGen1StatusLabels(_statuses, device);

	$: combinedLabelList = createGen1CombinedLabelList(timerLabels, labelList);
	$: mainLabel = extractGen1MainLabel(_statuses);
	$: statusSlots = resolveBluetoothSensorStatusSlots(device, _statuses);
	$: avatarStatus = statusSlots.avatar;
	$: heroStatus = statusSlots.hero;
	$: showLastReporter = !device.shared;
	$: showPeek = statusSlots.peek.length > 0 || showLastReporter;
	$: showFloating = !!heroStatus || showPeek;
	$: usePeekLayout = !!avatarStatus || showFloating;
</script>

<DeviceViewStateWrapper
	{device}
	hideSlotStaticBottom={usePeekLayout && !isGasSensor}
	hideSlotFloatingBottom={!showFloating}
>
	<div class="label-list-wrapper mb-s" slot="staticTop">
		{#if !usePeekLayout && canShowGen1LabelList(device, status)}
			<DeviceLabelList labelList={combinedLabelList} />
		{/if}
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<svelte:fragment slot="content">
		{#if avatarStatus}
			<div class="flex-full-height align-items-center justify-content-center">
				<div
					class="d-flex flex-direction-column align-items-center gap-s"
					role="img"
					aria-label="{getSidepanelStatusTitle(avatarStatus)} {getSidepanelStatusValue(avatarStatus)}"
				>
					<CircleAvatar
						size="xxl"
						icon={mapIconToThin(avatarStatus.icon)}
						backgroundColor="color-0"
					/>
					<Text styleType="h1">{capFirstChar(getSidepanelStatusValue(avatarStatus))}</Text>
					<Text styleType="miniature" class="text-color-muted">
						{getSidepanelStatusTitle(avatarStatus)}
					</Text>
				</div>
			</div>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="floatingBottom">
		{#if showFloating}
			<SidepanelStatusPeek
				showMain={!!heroStatus}
				main={heroStatus}
				peek={statusSlots.peek}
				forcePeekTray={showLastReporter}
			>
				<svelte:fragment slot="trayExtra">
					{#if showLastReporter}
						<LastReporterV2 class="mb-s" {device} />
					{/if}
				</svelte:fragment>
			</SidepanelStatusPeek>
		{/if}
	</svelte:fragment>

	<div class="flex-full-height justify-content-end" slot="staticBottom">
		{#if !usePeekLayout && mainLabel}
			{@const isCriticalEvent = checkIsCriticalEvent(mainLabel.value)}
			<ParameterCardV2
				class="mb-s"
				icon={mapIconToThin(mainLabel.icon)}
				title={capFirstChar(mainLabel.value)}
				description={capFirstChar(mainLabel.translation)}
				backgroundColor={isCriticalEvent ? '--color-danger-10' : '--color-0'}
			/>
		{/if}

		{#if isGasSensor}
			<div class="flex-full-height justify-content-end">
				<ControlGasSensorMute {device} />
			</div>
		{/if}
	</div>
</DeviceViewStateWrapper>
